/**
 * WallSegmentCollection - מחלקה לניהול אוסף של קטעי קיר
 * 
 * מחלקה זו מספקת ממשק ושיטות יעילות לשמירה, חיפוש ועדכון של קטעי קיר
 * כולל אפשרות לסינון מרחבי ושאילתות מהירות לפי מיקום
 */

import WallSegment from './WallSegment';

// קבועים למערכת הקטעים
const WALL_SEGMENT_CONSTANTS = {
  // סף הדמיון (Coincidence) לנקודות - אם המרחק קטן מזה נחשב כאותה נקודה
  POINT_COINCIDENCE_THRESHOLD: 0.1,
  
  // סף המקביליות - מוגדר כמכפלה וקטורית של וקטורי הכיוון של הקטעים
  PARALLELISM_THRESHOLD: 0.001,
  
  // סף הנראות המינימלי - קטעים קטנים מזה (באורך) יוסרו אוטומטית
  MIN_SEGMENT_LENGTH: 0.5,
  
  // מרחק מינימלי בין שני קטעים בכדי לחשב חיתוך
  MIN_SEGMENT_DISTANCE: 0.01
};

class WallSegmentCollection {
  /**
   * יוצר אוסף חדש של קטעי קיר
   * @param {Array<WallSegment>} initialSegments - קטעים התחלתיים (אופציונלי)
   */
  constructor(initialSegments = []) {
    // מערך של כל הקטעים
    this.segments = [...initialSegments];
    
    // מיפוי לפי מזהה עבור גישה מהירה
    this.segmentsById = new Map();
    initialSegments.forEach(segment => {
      this.segmentsById.set(segment.id, segment);
    });
    
    // מיפוי של קטעים לפי צורה מקורית (parentId)
    this.segmentsByParent = new Map();
    initialSegments.forEach(segment => {
      if (segment.parentId) {
        if (!this.segmentsByParent.has(segment.parentId)) {
          this.segmentsByParent.set(segment.parentId, []);
        }
        this.segmentsByParent.get(segment.parentId).push(segment);
      }
    });
    
    // האם המבנה המרחבי מעודכן
    this.spatialIndexNeedsUpdate = true;
    
    // כברירת מחדל, נשתמש בגריד פשוט כמבנה מרחבי
    // (יוחלף מאוחר יותר ב-Quadtree או מבנה מרחבי אחר)
    this.gridSize = 100; // גודל תא בגריד
    this.spatialGrid = new Map(); // מיפוי של תאים לקטעים
  }
  
  /**
   * מוסיף קטע חדש לאוסף
   * @param {WallSegment} segment - הקטע להוספה
   * @returns {WallSegment} הקטע שהוסף
   */
  addSegment(segment) {
    this.segments.push(segment);
    this.segmentsById.set(segment.id, segment);
    
    // הוספה למיפוי לפי הורה
    if (segment.parentId) {
      if (!this.segmentsByParent.has(segment.parentId)) {
        this.segmentsByParent.set(segment.parentId, []);
      }
      this.segmentsByParent.get(segment.parentId).push(segment);
    }
    
    // סימון שהמבנה המרחבי דורש עדכון
    this.spatialIndexNeedsUpdate = true;
    
    return segment;
  }
  
  /**
   * מוסיף מספר קטעים לאוסף
   * @param {Array<WallSegment>} segments - הקטעים להוספה
   */
  addSegments(segments) {
    segments.forEach(segment => this.addSegment(segment));
  }
  
  /**
   * מסיר קטע מהאוסף לפי מזהה
   * @param {string} segmentId - מזהה הקטע להסרה
   * @returns {boolean} האם ההסרה הצליחה
   */
  removeSegment(segmentId) {
    const segment = this.segmentsById.get(segmentId);
    if (!segment) return false;
    
    // הסרה מהמערך הראשי
    const index = this.segments.findIndex(s => s.id === segmentId);
    if (index !== -1) {
      this.segments.splice(index, 1);
    }
    
    // הסרה מהמיפוי לפי מזהה
    this.segmentsById.delete(segmentId);
    
    // הסרה מהמיפוי לפי הורה
    if (segment.parentId && this.segmentsByParent.has(segment.parentId)) {
      const parentSegments = this.segmentsByParent.get(segment.parentId);
      const parentIndex = parentSegments.findIndex(s => s.id === segmentId);
      if (parentIndex !== -1) {
        parentSegments.splice(parentIndex, 1);
      }
      
      // אם אין עוד קטעים לאותו הורה, הסר את הרשומה
      if (parentSegments.length === 0) {
        this.segmentsByParent.delete(segment.parentId);
      }
    }
    
    // סימון שהמבנה המרחבי דורש עדכון
    this.spatialIndexNeedsUpdate = true;
    
    return true;
  }
  
  /**
   * מוחק את כל הקטעים המשויכים לצורה מסוימת
   * @param {string} parentId - מזהה הצורה ההורה
   * @returns {number} מספר הקטעים שהוסרו
   */
  removeSegmentsByParent(parentId) {
    if (!this.segmentsByParent.has(parentId)) return 0;
    
    const segments = this.segmentsByParent.get(parentId);
    const removedCount = segments.length;
    
    // הסרת כל הקטעים
    segments.forEach(segment => {
      // הסרה מהמערך הראשי
      const index = this.segments.findIndex(s => s.id === segment.id);
      if (index !== -1) {
        this.segments.splice(index, 1);
      }
      
      // הסרה מהמיפוי לפי מזהה
      this.segmentsById.delete(segment.id);
    });
    
    // הסרה מהמיפוי לפי הורה
    this.segmentsByParent.delete(parentId);
    
    // סימון שהמבנה המרחבי דורש עדכון
    this.spatialIndexNeedsUpdate = true;
    
    return removedCount;
  }
  
  /**
   * מחזיר קטע לפי מזהה
   * @param {string} segmentId - מזהה הקטע
   * @returns {WallSegment|undefined} הקטע המבוקש
   */
  getSegmentById(segmentId) {
    return this.segmentsById.get(segmentId);
  }
  
  /**
   * מחזיר את כל הקטעים של צורה מסוימת
   * @param {string} parentId - מזהה הצורה ההורה
   * @returns {Array<WallSegment>} רשימת הקטעים
   */
  getSegmentsByParent(parentId) {
    return this.segmentsByParent.get(parentId) || [];
  }
  
  /**
   * בניית המבנה המרחבי (spatial index) אם צריך
   * מימוש פשוט מבוסס גריד - יוחלף מאוחר יותר בQuadtree או מבנה מתקדם יותר
   */
  updateSpatialIndex() {
    if (!this.spatialIndexNeedsUpdate) return;
    
    // איפוס המבנה המרחבי
    this.spatialGrid.clear();
    
    // הוספת כל הקטעים למבנה המרחבי
    this.segments.forEach(segment => {
      // מציאת תאי הגריד שהקטע עובר דרכם
      const cellsOverlapped = this.getCellsForSegment(segment);
      
      // הוספת הקטע לכל תא
      cellsOverlapped.forEach(cellKey => {
        if (!this.spatialGrid.has(cellKey)) {
          this.spatialGrid.set(cellKey, []);
        }
        this.spatialGrid.get(cellKey).push(segment);
      });
    });
    
    this.spatialIndexNeedsUpdate = false;
  }
  
  /**
   * מחשב את כל תאי הגריד שקטע עובר דרכם
   * @param {WallSegment} segment - הקטע לבדיקה
   * @returns {Array<string>} מערך של מפתחות תאים
   */
  getCellsForSegment(segment) {
    const { x1, y1, x2, y2 } = segment;
    const gridSize = this.gridSize;
    
    // מציאת קואורדינטות התאים של נקודות הקצה
    const startCellX = Math.floor(x1 / gridSize);
    const startCellY = Math.floor(y1 / gridSize);
    const endCellX = Math.floor(x2 / gridSize);
    const endCellY = Math.floor(y2 / gridSize);
    
    // מערך לשמירת מפתחות התאים
    const cells = new Set();
    
    // אם שתי הנקודות באותו תא, הוסף רק אותו
    if (startCellX === endCellX && startCellY === endCellY) {
      cells.add(`${startCellX},${startCellY}`);
      return Array.from(cells);
    }
    
    // אחרת, נמצא את כל התאים שהקטע עובר דרכם
    // אלגוריתם ברזנהם (Bresenham's line algorithm) לסריקת התאים
    let x = startCellX;
    let y = startCellY;
    const dx = Math.abs(endCellX - startCellX);
    const dy = Math.abs(endCellY - startCellY);
    const sx = startCellX < endCellX ? 1 : -1;
    const sy = startCellY < endCellY ? 1 : -1;
    let err = dx - dy;
    
    while (true) {
      cells.add(`${x},${y}`);
      
      if (x === endCellX && y === endCellY) break;
      
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
    
    return Array.from(cells);
  }
  
  /**
   * מחזיר את כל הקטעים באזור מסוים
   * @param {number} minX - גבול שמאלי של האזור
   * @param {number} minY - גבול עליון של האזור
   * @param {number} maxX - גבול ימני של האזור
   * @param {number} maxY - גבול תחתון של האזור
   * @returns {Array<WallSegment>} רשימת הקטעים באזור
   */
  getSegmentsInArea(minX, minY, maxX, maxY) {
    // עדכון המבנה המרחבי אם צריך
    this.updateSpatialIndex();
    
    // מציאת תאי הגריד שחופפים לאזור
    const minCellX = Math.floor(minX / this.gridSize);
    const minCellY = Math.floor(minY / this.gridSize);
    const maxCellX = Math.floor(maxX / this.gridSize);
    const maxCellY = Math.floor(maxY / this.gridSize);
    
    // אוסף הקטעים הייחודיים שנמצאו
    const uniqueSegments = new Set();
    
    // בדיקת כל התאים באזור
    for (let x = minCellX; x <= maxCellX; x++) {
      for (let y = minCellY; y <= maxCellY; y++) {
        const cellKey = `${x},${y}`;
        
        if (this.spatialGrid.has(cellKey)) {
          const cellSegments = this.spatialGrid.get(cellKey);
          
          // הוספת כל הקטעים בתא שחופפים לאזור
          cellSegments.forEach(segment => {
            // בדיקה יותר מדויקת אם הקטע באמת חופף לאזור
            if (this.segmentIntersectsBox(segment, minX, minY, maxX, maxY)) {
              uniqueSegments.add(segment);
            }
          });
        }
      }
    }
    
    return Array.from(uniqueSegments);
  }
  
  /**
   * בודק אם קטע חופף לתיבה מוגדרת
   * @param {WallSegment} segment - הקטע לבדיקה
   * @param {number} minX - הגבול השמאלי של התיבה
   * @param {number} minY - הגבול העליון של התיבה
   * @param {number} maxX - הגבול הימני של התיבה
   * @param {number} maxY - הגבול התחתון של התיבה
   * @returns {boolean} האם הקטע חופף לתיבה
   */
  segmentIntersectsBox(segment, minX, minY, maxX, maxY) {
    const { x1, y1, x2, y2 } = segment;
    
    // בדיקה אם לפחות אחת מנקודות הקצה בתוך התיבה
    const point1Inside = (x1 >= minX && x1 <= maxX && y1 >= minY && y1 <= maxY);
    const point2Inside = (x2 >= minX && x2 <= maxX && y2 >= minY && y2 <= maxY);
    
    if (point1Inside || point2Inside) return true;
    
    // בדיקה אם הקטע חוצה את אחת מצלעות התיבה
    
    // צד שמאל של התיבה
    if (this.lineIntersection(x1, y1, x2, y2, minX, minY, minX, maxY)) return true;
    
    // צד ימין של התיבה
    if (this.lineIntersection(x1, y1, x2, y2, maxX, minY, maxX, maxY)) return true;
    
    // צד עליון של התיבה
    if (this.lineIntersection(x1, y1, x2, y2, minX, minY, maxX, minY)) return true;
    
    // צד תחתון של התיבה
    if (this.lineIntersection(x1, y1, x2, y2, minX, maxY, maxX, maxY)) return true;
    
    return false;
  }
  
  /**
   * בדיקת חיתוך בין שני קטעים
   * @param {number} x1 - קואורדינטת X של תחילת הקטע הראשון
   * @param {number} y1 - קואורדינטת Y של תחילת הקטע הראשון
   * @param {number} x2 - קואורדינטת X של סוף הקטע הראשון
   * @param {number} y2 - קואורדינטת Y של סוף הקטע הראשון
   * @param {number} x3 - קואורדינטת X של תחילת הקטע השני
   * @param {number} y3 - קואורדינטת Y של תחילת הקטע השני
   * @param {number} x4 - קואורדינטת X של סוף הקטע השני
   * @param {number} y4 - קואורדינטת Y של סוף הקטע השני
   * @returns {boolean} האם הקטעים נחתכים
   */
  lineIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
    // חישוב מכפלת וקטורים צולבת
    const ccw = (x1, y1, x2, y2, x3, y3) => {
      return (y3 - y1) * (x2 - x1) > (y2 - y1) * (x3 - x1);
    };
    
    return (ccw(x1, y1, x3, y3, x4, y4) !== ccw(x2, y2, x3, y3, x4, y4)) &&
           (ccw(x1, y1, x2, y2, x3, y3) !== ccw(x1, y1, x2, y2, x4, y4));
  }
  
  /**
   * בודק אם קטע נמצא בטווח מוגדר מנקודה
   * משמש למחיקה בעזרת כלי המחק
   * @param {WallSegment} segment - הקטע לבדיקה
   * @param {number} x - קואורדינטת X של הנקודה
   * @param {number} y - קואורדינטת Y של הנקודה
   * @param {number} radius - הרדיוס לבדיקה
   * @returns {boolean} האם הקטע נמצא בטווח הרדיוס
   */
  isSegmentInRange(segment, x, y, radius) {
    // חישוב המרחק המינימלי בין הקטע לנקודה
    const distance = segment.distanceToPoint(x, y);
    
    // בדיקה אם המרחק קטן מהרדיוס
    return distance <= radius;
  }
  
  /**
   * מוצא את כל הקטעים בטווח מוגדר מנקודה
   * @param {number} x - קואורדינטת X של הנקודה
   * @param {number} y - קואורדינטת Y של הנקודה
   * @param {number} radius - הרדיוס לחיפוש
   * @returns {Array<WallSegment>} רשימת הקטעים בטווח
   */
  getSegmentsInRange(x, y, radius) {
    // הגדרת תיבה המקיפה את העיגול
    const minX = x - radius;
    const minY = y - radius;
    const maxX = x + radius;
    const maxY = y + radius;
    
    // קבלת כל הקטעים באזור התיבה
    const candidateSegments = this.getSegmentsInArea(minX, minY, maxX, maxY);
    
    // סינון רק הקטעים שבאמת בטווח הרדיוס
    return candidateSegments.filter(segment => this.isSegmentInRange(segment, x, y, radius));
  }
  
  /**
   * מוחק את כל הקטעים שנמצאים במסלול המחק
   * @param {Array<{x: number, y: number, radius: number}>} eraserPath - מסלול המחק
   * @returns {Array<WallSegment>} הקטעים שנוצרו לאחר המחיקה (תמיד ריק בגישה החדשה)
   */
  eraseSegments(eraserPath) {
    // בגישה החדשה של "מה שאתה רואה הוא מה שאתה מקבל",
    // הפונקציה הזו לא צריכה לבצע שינויים באוסף הקטעים.
    // קטעי הקיר יחושבו מחדש על בסיס השכבות הגלויות.
    // לכן, היא פשוט מחזירה מערך ריק.
    return [];
  }
  
  /**
   * המרה של האוסף למבנה נתונים פשוט לשמירה
   * @returns {Array<Object>} מערך של אובייקטים המייצגים את הקטעים
   */
  toJSON() {
    return this.segments.map(segment => segment.toJSON());
  }
  
  /**
   * יצירת אוסף קטעים מתוך נתוני JSON
   * @param {Array<Object>} data - מערך של אובייקטי JSON המייצגים קטעים
   * @returns {WallSegmentCollection} אוסף קטעים חדש
   */
  static fromJSON(data) {
    const segments = data.map(segmentData => WallSegment.fromJSON(segmentData));
    return new WallSegmentCollection(segments);
  }
  
  /**
   * בודק אם שתי נקודות נחשבות לאותה נקודה (מספיק קרובות)
   * @param {number} x1 - קואורדינטת X של הנקודה הראשונה
   * @param {number} y1 - קואורדינטת Y של הנקודה הראשונה
   * @param {number} x2 - קואורדינטת X של הנקודה השנייה
   * @param {number} y2 - קואורדינטת Y של הנקודה השנייה
   * @returns {boolean} האם הנקודות נחשבות לאותה נקודה
   */
  arePointsCoincident(x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const distSquared = dx * dx + dy * dy;
    
    return distSquared < WALL_SEGMENT_CONSTANTS.POINT_COINCIDENCE_THRESHOLD * WALL_SEGMENT_CONSTANTS.POINT_COINCIDENCE_THRESHOLD;
  }
  
  /**
   * בודק אם שני קטעים חופפים (מקבילים ומכסים חלק אחד של השני)
   * @param {WallSegment} segment1 - הקטע הראשון
   * @param {WallSegment} segment2 - הקטע השני
   * @returns {boolean} האם הקטעים חופפים
   */
  areSegmentsOverlapping(segment1, segment2) {
    // בדיקה אם הקטעים מקבילים
    const dx1 = segment1.x2 - segment1.x1;
    const dy1 = segment1.y2 - segment1.y1;
    const dx2 = segment2.x2 - segment2.x1;
    const dy2 = segment2.y2 - segment2.y1;
    
    // נרמול וקטורי הכיוון
    const length1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const length2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
    
    if (length1 === 0 || length2 === 0) return false; // אחד הקטעים הוא נקודה
    
    const nx1 = dx1 / length1;
    const ny1 = dy1 / length1;
    const nx2 = dx2 / length2;
    const ny2 = dy2 / length2;
    
    // חישוב מכפלה סקלרית של וקטורי הכיוון
    const dotProduct = nx1 * nx2 + ny1 * ny2;
    
    // בדיקה אם הוקטורים מקבילים (מכפלה סקלרית קרובה ל-1 או -1)
    if (Math.abs(Math.abs(dotProduct) - 1) > WALL_SEGMENT_CONSTANTS.PARALLELISM_THRESHOLD) {
      return false; // לא מקבילים
    }
    
    // ---- NEW: Check for collinearity if parallel ----
    // Calculate perpendicular distance from segment2.start to the line of segment1
    // Perpendicular distance = |(x2-x1)(y1-y0) - (x1-x0)(y2-y1)| / sqrt((x2-x1)^2 + (y2-y1)^2)
    // Here, (x0,y0) is segment2.x1, segment2.y1
    // (x1,y1) is segment1.x1, segment1.y1
    // (x2,y2) is segment1.x2, segment1.y2
    const s1x1 = segment1.x1, s1y1 = segment1.y1, s1x2 = segment1.x2, s1y2 = segment1.y2;
    const s2x1 = segment2.x1, s2y1 = segment2.y1;

    const numerator = Math.abs((s1x2 - s1x1) * (s1y1 - s2y1) - (s1x1 - s2x1) * (s1y2 - s1y1));
    // length1 is already calculated as sqrt((s1x2-s1x1)^2 + (s1y2-s1y1)^2)
    if (length1 > WALL_SEGMENT_CONSTANTS.POINT_COINCIDENCE_THRESHOLD) { // Avoid division by zero for point-like segment1
        const perpendicularDistance = numerator / length1;
        if (perpendicularDistance > WALL_SEGMENT_CONSTANTS.POINT_COINCIDENCE_THRESHOLD) {
            return false; // Parallel but not collinear enough
        }
    } else {
        // segment1 is a point. Check distance from segment2.start to this point.
        const distToPoint = Math.sqrt(Math.pow(s2x1 - s1x1, 2) + Math.pow(s2y1 - s1y1, 2));
        if (distToPoint > WALL_SEGMENT_CONSTANTS.POINT_COINCIDENCE_THRESHOLD) {
            return false; // segment1 is a point, and segment2.start is not coincident with it
        }
        // If segment1 is a point and segment2.start is coincident, they could be considered overlapping if segment2 also covers this point.
        // The existing projection logic below should handle this if segment2 is also very short or a point.
    }
    // ---- END NEW ----

    // בדיקה אם הקטעים על אותו קו
    // הסט את הכל כך שנקודת ההתחלה של הקטע הראשון תהיה בראשית הצירים
    const projectionToSegment1 = (x, y) => {
      // מציאת מיקום הנקודה על הקטע (פרויקציה)
      const t = ((x - segment1.x1) * nx1 + (y - segment1.y1) * ny1) / length1;
      return t;
    };
    
    // חישוב ערכי t לארבע נקודות הקצה
    const t_s2_start = projectionToSegment1(segment2.x1, segment2.y1);
    const t_s2_end = projectionToSegment1(segment2.x2, segment2.y2);
    
    // בדיקה אם יש חפיפה
    // אם אחת מנקודות הקטע השני בתוך הקטע הראשון
    return (t_s2_start >= 0 && t_s2_start <= 1) || (t_s2_end >= 0 && t_s2_end <= 1) ||
           // או אם שתי נקודות הקטע הראשון בתוך הקטע השני
           (t_s2_start <= 0 && t_s2_end >= 1);
  }
  
  /**
   * מאחד קטעים חופפים למניעת כפילויות
   * @returns {number} מספר הקטעים שאוחדו
   */
  mergeOverlappingSegments() {
    // מערך להחזקת מזהים של קטעים שאוחדו
    const mergedSegmentIds = new Set();
    
    // מערך של קטעים חדשים לאחר האיחוד
    const newSegments = [];
    
    // בדיקה של כל זוג אפשרי של קטעים
    for (let i = 0; i < this.segments.length; i++) {
      // אם הקטע כבר אוחד, דלג
      if (mergedSegmentIds.has(this.segments[i].id)) continue;
      
      const segment1 = this.segments[i];
      let mergedSegment = null;
      
      // חפש קטעים שחופפים לקטע הנוכחי
      for (let j = i + 1; j < this.segments.length; j++) {
        // אם הקטע השני כבר אוחד, דלג
        if (mergedSegmentIds.has(this.segments[j].id)) continue;
        
        const segment2 = this.segments[j];
        
        // בדיקה אם הקטעים חופפים
        if (this.areSegmentsOverlapping(segment1, segment2)) {
          // סימון הקטע השני כמאוחד
          mergedSegmentIds.add(segment2.id);
          
          // אם אין עדיין קטע מאוחד, צור אחד חדש
          if (!mergedSegment) {
            mergedSegment = this.mergeSegments(segment1, segment2);
            mergedSegmentIds.add(segment1.id); // סימון גם הקטע הראשון כמאוחד
          } else {
            // אחרת, אחד את הקטע השני עם הקטע המאוחד
            mergedSegment = this.mergeSegments(mergedSegment, segment2);
          }
        }
      }
      
      // אם יש קטע מאוחד, הוסף אותו לקטעים החדשים
      if (mergedSegment) {
        newSegments.push(mergedSegment);
      } else if (!mergedSegmentIds.has(segment1.id)) {
        // אם אין קטע מאוחד, שמור את הקטע המקורי
        // (לא נוסף עדיין לקטעים החדשים)
        newSegments.push(segment1);
      }
    }
    
    // הסר את הקטעים שאוחדו
    mergedSegmentIds.forEach(segmentId => {
      this.removeSegment(segmentId);
    });
    
    // הוסף את הקטעים המאוחדים
    this.addSegments(newSegments);
    
    return mergedSegmentIds.size; // החזר את מספר הקטעים שאוחדו
  }
  
  /**
   * מאחד שני קטעים חופפים לקטע אחד
   * @param {WallSegment} segment1 - הקטע הראשון
   * @param {WallSegment} segment2 - הקטע השני
   * @returns {WallSegment} הקטע המאוחד
   */
  mergeSegments(segment1, segment2) {
    // מחשב את הנקודות הקיצוניות של שני הקטעים
    const points = [
      { x: segment1.x1, y: segment1.y1 },
      { x: segment1.x2, y: segment1.y2 },
      { x: segment2.x1, y: segment2.y1 },
      { x: segment2.x2, y: segment2.y2 }
    ];
    
    // מחשב את וקטור הכיוון של הקטע הראשון
    const dx = segment1.x2 - segment1.x1;
    const dy = segment1.y2 - segment1.y1;
    const length = Math.sqrt(dx * dx + dy * dy);
    
    if (length === 0) return segment1; // אם הקטע הראשון הוא נקודה, החזר אותו
    
    const nx = dx / length;
    const ny = dy / length;
    
    // פרויקציות של כל הנקודות על הקו של הקטע הראשון
    const projections = points.map(point => {
      const t = ((point.x - segment1.x1) * nx + (point.y - segment1.y1) * ny) / length;
      return { point, t };
    });
    
    // מיון הפרויקציות לפי המיקום על הקו
    projections.sort((a, b) => a.t - b.t);
    
    // לקיחת הנקודות הקיצוניות (הראשונה והאחרונה)
    const startPoint = projections[0].point;
    const endPoint = projections[projections.length - 1].point;
    
    // העובי של הקטע המאוחד - בינתיים לוקחים את העובי של הקטע הראשון
    // (אפשר לשנות ממוצע או ערך אחר)
    const thickness = segment1.thickness;
    
    // יצירת קטע חדש עם הנקודות הקיצוניות
    return new WallSegment(
      startPoint.x, startPoint.y,
      endPoint.x, endPoint.y,
      {
        thickness,
        color: segment1.color,
        type: segment1.type,
        parentId: segment1.parentId // או null אם רוצים לנתק מההורה המקורי
      }
    );
  }
  
  /**
   * מסיר קטעים קצרים מדי שקשה/בלתי אפשרי לראות
   * @param {number} minLength - האורך המינימלי לשמירה (ברירת מחדל: ערך קבוע)
   * @returns {number} מספר הקטעים שהוסרו
   */
  removeShortSegments(minLength = WALL_SEGMENT_CONSTANTS.MIN_SEGMENT_LENGTH) {
    const segmentsToRemove = [];
    
    // איתור קטעים קצרים מדי
    this.segments.forEach(segment => {
      const dx = segment.x2 - segment.x1;
      const dy = segment.y2 - segment.y1;
      const lengthSquared = dx * dx + dy * dy;
      
      if (lengthSquared < minLength * minLength) {
        segmentsToRemove.push(segment.id);
      }
    });
    
    // הסרת הקטעים הקצרים
    segmentsToRemove.forEach(segmentId => {
      this.removeSegment(segmentId);
    });
    
    return segmentsToRemove.length;
  }
  
  /**
   * מטפל במקרי קצה שונים כגון קטעים חופפים ומאוחדים, וקטעים קצרים מדי
   * @param {Object} options - אפשרויות לטיפול
   * @param {boolean} options.mergeOverlapping - האם לאחד קטעים חופפים (ברירת מחדל: true)
   * @param {boolean} options.removeShort - האם להסיר קטעים קצרים (ברירת מחדל: true)
   * @param {number} options.minLength - האורך המינימלי לשמירה (ברירת מחדל: ערך קבוע)
   * @returns {Object} סטטיסטיקה על הטיפול: מספר הקטעים שאוחדו, מספר הקטעים שהוסרו
   */
  handleEdgeCases(options = {}) {
    const mergeOverlapping = options.mergeOverlapping !== undefined ? options.mergeOverlapping : true;
    const removeShort = options.removeShort !== undefined ? options.removeShort : true;
    const minLength = options.minLength || WALL_SEGMENT_CONSTANTS.MIN_SEGMENT_LENGTH;

    // סטטיסטיקה
    const stats = {
      mergedSegments: 0,
      removedShortSegments: 0
    };

    // const initialSegmentCount = this.segments.length; // Commented: unused debug variable

    // איחוד קטעים חופפים
    if (mergeOverlapping) {
      stats.mergedSegments = this.mergeOverlappingSegments();
    }

    // הסרת קטעים קצרים מדי
    if (removeShort) {
      stats.removedShortSegments = this.removeShortSegments(minLength);
    }

    // עדכון המבנה המרחבי אם בוצעו שינויים
    if (stats.mergedSegments > 0 || stats.removedShortSegments > 0) {
      this.spatialIndexNeedsUpdate = true;
    }

    return stats;
  }
  
  /**
   * בנייה של תצוגה מעובדת ואופטימלית של קטעים עבור תצוגה ברזולוציה נמוכה
   * @param {number} minLength - האורך המינימלי לשמירה ברזולוציה נמוכה
   * @returns {Array<WallSegment>} רשימת קטעים מעובדת לתצוגה ברזולוציה נמוכה
   */
  getLowResolutionSegments(minLength = 5) {
    // יצירת עותק של האוסף עבור התצוגה המיוחדת
    const tempCollection = new WallSegmentCollection([...this.segments]);
    
    // טיפול במקרי קצה
    tempCollection.handleEdgeCases({
      mergeOverlapping: true,
      removeShort: true,
      minLength
    });
    
    return tempCollection.segments;
  }
}

export default WallSegmentCollection; 